import { useEffect, useState } from 'react';

import type { CaseDetailDto } from '../../api/types';
import { fmtDate, fmtDateTime } from '../../lib/format';
import { stageLabel } from '../../lib/stages';

export interface TabDraftProps {
    detail: CaseDetailDto;
    busy: boolean;
    onGenerate: (again: boolean) => void;
    onSave: (text: string) => void;
    onToApproval: () => void;
    onSend: () => void;
}

/**
 * Вкладка «Проект ответа» (ТЗ §8.5.2). Селектор версий — разрыв 9.6 спека:
 * версии живут в `reply_drafts`, архивная версия открывается только на чтение.
 */
export function TabDraft({ detail, busy, onGenerate, onSave, onToApproval, onSend }: TabDraftProps) {
    const { case: item, drafts, permissions } = detail;

    const current = drafts.find((d) => d.is_current) ?? (drafts.length > 0 ? drafts[0] : null);
    const [version, setVersion] = useState<number | null>(null);
    const selected = version === null ? current : (drafts.find((d) => d.version === version) ?? current);
    const archived = selected !== null && current !== null && selected.version !== current.version;

    const [text, setText] = useState(selected ? selected.text : '');
    const syncKey = selected ? selected.id + '#' + selected.version : 'none';

    // Текст перечитывается только при смене версии или появлении новой,
    // иначе ввод пользователя затирался бы на каждом обновлении дела.
    useEffect(() => {
        setText(selected ? selected.text : '');
    }, [syncKey]);

    useEffect(() => {
        if (version !== null && !drafts.some((d) => d.version === version)) setVersion(null);
    }, [drafts, version]);

    const closed = item.stage === 'sent' || Boolean(item.closed_at);
    const readonly = !permissions.draft_save || archived;
    const canShowSend = !closed && (permissions.send || permissions.draft_save || permissions.to_approval);

    let note: string;
    if (archived) note = 'Просмотр архивной версии. Вернитесь к текущей, чтобы редактировать.';
    else if (closed) note = 'Дело закрыто, ответ направлен ' + (fmtDate(item.closed_at ?? item.sent_at) || '—') + '.';
    else if (item.approved) note = 'Проект согласован главным врачом — можно отправлять.';
    else if (!permissions.draft_save) note = 'Текст готовит исполнитель.';
    else note = 'Стадия: ' + (stageLabel(item.stage) || item.stage_label);

    return (
        <>
            <textarea
                className="draft-area"
                value={text}
                readOnly={readonly}
                aria-label="Текст проекта ответа"
                placeholder={
                    drafts.length === 0
                        ? 'Проекта ответа ещё нет. Сгенерируйте его или наберите текст вручную.'
                        : undefined
                }
                onChange={(event) => setText(event.target.value)}
            />
            <div className="draft-bar">
                {drafts.length > 0 ? (
                    <select
                        className="act-select"
                        aria-label="Версия проекта ответа"
                        value={selected ? String(selected.version) : ''}
                        onChange={(event) => {
                            const next = Number(event.target.value);
                            setVersion(current && next === current.version ? null : next);
                        }}
                    >
                        {drafts.map((draft) => (
                            <option key={draft.id} value={draft.version}>
                                {'Версия ' +
                                    draft.version +
                                    ' · ' +
                                    fmtDateTime(draft.created_at) +
                                    ' · ' +
                                    (draft.generated ? 'сформировано ИИ' : (draft.author_name ?? 'исполнитель')) +
                                    (draft.is_current ? ' (текущая)' : '')}
                            </option>
                        ))}
                    </select>
                ) : null}

                {permissions.draft_generate && !archived ? (
                    <button
                        type="button"
                        className="act-btn"
                        disabled={busy}
                        onClick={() => onGenerate(drafts.length > 0)}
                    >
                        {drafts.length > 0 ? '↻ Перегенерировать' : '⚙ Сгенерировать проект'}
                    </button>
                ) : null}

                {permissions.draft_save && archived ? (
                    <button
                        type="button"
                        className="act-btn"
                        disabled={busy}
                        onClick={() => onSave(text)}
                    >
                        ↩ Восстановить эту версию
                    </button>
                ) : null}

                {permissions.draft_save && !archived ? (
                    <button
                        type="button"
                        className="act-btn"
                        disabled={busy || text.trim().length === 0}
                        onClick={() => onSave(text)}
                    >
                        💾 Сохранить черновик
                    </button>
                ) : null}

                {permissions.to_approval && !archived && item.stage !== 'approval' ? (
                    <button type="button" className="act-btn primary" disabled={busy} onClick={onToApproval}>
                        📤 Отправить на согласование
                    </button>
                ) : null}

                {canShowSend ? (
                    <button
                        type="button"
                        className="act-btn primary"
                        disabled={busy || !item.approved || !permissions.send}
                        title={item.approved ? undefined : 'Доступно после согласования главным врачом'}
                        onClick={onSend}
                    >
                        📨 Отправить заявителю
                    </button>
                ) : null}

                <span className="draft-note">{note}</span>
            </div>
        </>
    );
}

export default TabDraft;
